import { lazy, ComponentType } from 'react';

/**
 * Drop-in replacement for React.lazy that hardens dynamic imports against the
 * two ways a code-split chunk fetch fails in production:
 *
 *  1. Transient network blip / CDN edge miss - retried once after a short
 *     delay before giving up.
 *  2. Stale deploy - the user is running an old index.html whose chunk hashes
 *     no longer exist on the CDN (a new deploy replaced them). The fetch 404s
 *     permanently; the only recovery is to load the new index.html. We force a
 *     single full-page reload, guarded by sessionStorage so a genuinely broken
 *     chunk can never cause a reload loop.
 *
 * Without this, a rejected lazy import throws during render. For route-level
 * boundaries that degrades one route; for the lazy modals mounted in the eager
 * app shell it would white-screen the entire app.
 */

const RELOAD_GUARD_PREFIX = 'chunk-reload:';

type Factory<T> = () => Promise<{ default: ComponentType<T> }>;

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

export default function lazyWithRetry<T>(
  factory: Factory<T>,
  // A stable key per call site so the reload guard is scoped, not global.
  // Defaults to the factory source, which is stable for a given import().
  guardKey: string = factory.toString(),
) {
  return lazy<ComponentType<T>>(async () => {
    const storageKey = RELOAD_GUARD_PREFIX + guardKey;

    try {
      const mod = await factory();
      // Success - clear any prior reload flag for this chunk.
      try {
        window.sessionStorage?.removeItem(storageKey);
      } catch {
        /* sessionStorage may be unavailable (private mode) - ignore */
      }
      return mod;
    } catch (firstError) {
      // One retry for a transient failure.
      try {
        await wait(400);
        return await factory();
      } catch (secondError) {
        // Persistent failure. If we have not already tried reloading for this
        // chunk, assume a stale deploy and reload once to fetch fresh assets.
        let alreadyReloaded = false;
        try {
          alreadyReloaded =
            window.sessionStorage?.getItem(storageKey) === 'true';
        } catch {
          /* ignore */
        }

        if (!alreadyReloaded) {
          try {
            window.sessionStorage?.setItem(storageKey, 'true');
          } catch {
            /* ignore */
          }
          window.location.reload();
          // Return a never-resolving promise so React keeps showing the
          // Suspense fallback during the reload instead of throwing.
          return new Promise(() => {});
        }

        // Already reloaded once and it still fails - this is a genuinely
        // broken chunk, not a stale deploy. Let the error propagate to the
        // nearest error boundary / Suspense error path.
        throw secondError;
      }
    }
  });
}
